/* eslint-disable @typescript-eslint/no-unused-vars */
import React from "react";
import { SquareButton } from "@themuse/design-system";

import { RichTextModule } from "components/RichTextModule";
import { ContentModuleBaseAttributes } from "utils/orm";
import { ResponsiveImage } from "components/ResponsiveImage";
import { getId } from "utils/strings";

import styles from "./company.module.scss";

export interface Company {
    description: string;
    id: number;
    isArticle: boolean;
    legacyId: number;
    logoImage: string;
    primaryImage: string;
    secondaryImage: string;
    shortName: string;
    slug: string;
    tertiaryImage: string;
    title: string;
}

export interface CompanyProfileBlock extends ContentModuleBaseAttributes {
    company: Company;
    isArticle: boolean;
    orderNum?: number;
    replaceDescription: boolean;
    richText: string;
    sortOrder?: number;
}

export interface DescriptionProps {
    description: string;
    isArticle: boolean;
    replaceDescription: boolean;
    richText: string;
}

export interface HeaderProps {
    isArticle: boolean;
    companyName: string;
    sortOrder: number;
    shortName: string;
}

const Description: React.FC<DescriptionProps> = ({
    isArticle, description, replaceDescription, richText
}) => {
    const Extended = () => (richText ? <RichTextModule type="richTextBlock" className="extended" richText={richText} /> : null);
    const Static = () => (description ? <RichTextModule type="richTextBlock" className="legacy" richText={description} /> : null);
    const Both = () => (
        <>
            <Static />
            <Extended />
        </>
    );

    return <>{isArticle ? null : <div className={styles.description}>{replaceDescription ? <Extended /> : <Both />}</div>}</>;
};

/**
 * Creates a company module within the page.
 * Passing an order number will render a listicle style with both a heading and description.
 * @example
 * <CompanyModule {...MOCK_COMPANY} />
 * <CompanyModule isArticle={true}{...MOCK_COMPANY} />
 */
export const CompanyModule: React.FC<CompanyProfileBlock> = (props) => {
    const {
        isArticle, replaceDescription, richText, orderNum, company
    } = props;
    const {
        shortName, primaryImage, secondaryImage, tertiaryImage, description, title, slug, legacyId, id
    } = company;
    const aspect = 0.73896353167;
    const alt = {
        profile: `${title} company profile`,
        culture: `${title}  culture`,
        working: `Working at ${title}`
    };

    const impressionTrackingProps = {
        content_type: "module",
        company_name: title,
        company_short_name: slug,
        company_id: legacyId,
        company_craft_id: id,
        placement_position: orderNum,
        sp_content_is_tile: true,
        sp_tile_entity_id: getId(legacyId).toString(),
        sp_tile_order: orderNum,
        sp_tile_type: "round_up",
        sp_tile_company_id: legacyId.toString(),
        sp_impression_component: "tile",
        sp_impression_position: "in_article",
        sp_impression_tile_type: "round_up",
        sp_page_section: "advice",
        sp_page_type: "content"
    };
    const serializedImpressionTrackingProps = JSON.stringify(impressionTrackingProps);
    const clickTrackingProps = {
        sp_click_component: "button",
        sp_click_component_tile_type: "round_up",
        sp_click_insert_type: null,
        sp_click_position: "main",
        sp_click_search_id: null,
        sp_page_type: "content",
        sp_page_section: "advice",
        sp_page_company_id: legacyId.toString(),
        sp_page_tab: null,
        sp_is_external_page: false,
        sp_page_entity_id: id,
        sp_tile_entity_id: getId(legacyId).toString(),
        sp_tile_order: orderNum,
        sp_tile_type: "round_up",
        sp_tile_company_id: legacyId.toString(),
    };
    const profileTrackingProps = {
        ...clickTrackingProps,
        sp_click_button_text: "view profile",
        sp_click_button_type: "profile",
        sp_click_component: "button",
        sp_click_target_page_section: "profile",
    };
    const searchTrackingProps = {
        ...clickTrackingProps,
        sp_click_button_text: "explore jobs",
        sp_click_button_type: "job_search",
        sp_click_component: "button",
        sp_click_target_page_section: "search",
    };
    const imageTrackingProps = {
        ...clickTrackingProps,
        sp_click_component: "tile",
        sp_tile_entity_id: getId(legacyId).toString(),
        sp_tile_order: orderNum,
        sp_tile_company_id: legacyId.toString(),
        sp_click_target_page_section: "profile"
    };
    const imageTrackingData = {
        "data-tracking-event-type": "click",
        "data-tracking-props": JSON.stringify(imageTrackingProps)
    };
    const mobilePrimary = 325;
    const tabletPrimary = 447;
    const mobileSecondary = 217;
    const sizesPrimary = {
        mobileWidth: mobilePrimary,
        mobileHeight: Math.ceil(mobilePrimary * aspect),
        tabletWidth: tabletPrimary,
        tabletHeight: Math.ceil(tabletPrimary * aspect)
    };
    const sizesSecondary = {
        mobileWidth: mobileSecondary,
        mobileHeight: Math.ceil(mobileSecondary * aspect)
    };
    const profileUrl = `/profiles/${shortName}`;
    const jobsUrl = `/search/company/${shortName}`;

    return (
        <section
            data-oskar-click="adviceCompanyModuleClicked"
            data-oskar-view="adviceCompanyModuleViewed"
            data-oskar-props={serializedImpressionTrackingProps}
            data-tracking-event-type="impression"
            data-tracking-props={serializedImpressionTrackingProps}
        >
            <div>
                <figure className={styles.officeGrid}>
                    <div className={styles.primary}>
                        <a href={profileUrl}>
                            <ResponsiveImage
                                loading="lazy"
                                altText={alt.profile}
                                url={primaryImage}
                                {...sizesPrimary}
                                extraProps={imageTrackingData}
                            />
                        </a>
                    </div>

                    <div className={styles.secondary}>
                        <a
                            href={profileUrl}
                            data-tracking-event-type="click"
                            data-tracking-props={JSON.stringify(profileTrackingProps)}
                        >
                            <ResponsiveImage
                                extraProps={imageTrackingData}
                                loading="lazy"
                                altText={alt.culture}
                                url={secondaryImage}
                                {...sizesSecondary}
                            />
                        </a>
                    </div>

                    <div className={styles.tertiary}>
                        <a
                            href={profileUrl}
                            data-tracking-event-type="click"
                            data-tracking-props={JSON.stringify(profileTrackingProps)}
                        >
                            <ResponsiveImage
                                extraProps={imageTrackingData}
                                loading="lazy"
                                altText={alt.working}
                                url={tertiaryImage}
                                {...sizesSecondary}
                            />
                        </a>
                    </div>
                </figure>
                <div className={styles.ctaButtons}>
                    <SquareButton
                        className={styles.squareButton}
                        color="light-blue"
                        href={profileUrl}
                        target="_blank"
                        data-tracking-event-type="click"
                        data-tracking-props={JSON.stringify(profileTrackingProps)}
                    >
                        View Profile
                    </SquareButton>
                    <SquareButton
                        className={styles.squareButton}
                        color="mint"
                        href={jobsUrl}
                        target="_blank"
                        data-tracking-event-type="click"
                        data-tracking-props={JSON.stringify(searchTrackingProps)}
                    >
                        Explore
                        {" "}
                        <span className="hidden visible--sm">Open</span>
                        {" "}
                        Jobs
                    </SquareButton>
                </div>
                <Description {...{
                    isArticle, replaceDescription, description, richText
                }}
                />
            </div>
        </section>
    );
};
